<script lang="ts">  import { m } from '$lib/paraglide/messages';

  import { getLocaleFromPath, removeLocaleSlug, translation } from '$lib/locale/locale';
  import SearchFilters from '$lib/ui/search/search-filters.svelte';
  import TrackGrid from '$lib/ui/shared/track-grid.svelte';
  import AssetList from '$lib/ui/shared/asset-list.svelte';
  import { queryToSearch } from '$lib/utils/query';
  import type { Tag } from '$lib/models/tag';
  import { AssetType } from '$lib/utils/asset';
  import {
    filtersToSearch,
    isAnyRemovableFilterSelected,
    isSinglePrettyURLCategorySelected,
    maybeGetSingleSelectedChildCategory,
    searchToFilters,
  } from '$lib/utils/search';
  import SeoBottomBanner from '$lib/ui/search/seo-bottom-banner.svelte';
  import SearchHero from '$lib/ui/search/search-hero.svelte';
  import type { PageData } from './$types';
  import { searchContext } from '$lib/stores/search';
  import { getPathParts } from '$lib/utils/string';
  import { afterNavigate, goto } from '$app/navigation';
  import { scrollToTop } from '$lib/utils/scroll';
  import { CategoryType } from '$lib/utils/category';
  import { browser } from '$app/environment';
  import { userContext } from '$lib/stores/stores';
  import { trackSearch } from '$lib/tracking/tool';
  import { get } from 'svelte/store';
  import Shimmer from '$lib/ui/shared/shimmers/shimmer.svelte';
  import { api } from '$lib/api';
  import type { SearchResponse } from '$lib/models/search-response';
  import { onDestroy, onMount } from 'svelte';
  import Spinner from '$lib/ui/shared/spinner.svelte';
  import { wishlisted } from '$lib/state/wishlisted.svelte';
  import type { Filters } from '$lib/models/filters';
  import TopTabs from './top-tabs.svelte';

  const { langHref, locale } = translation();
  const user = userContext.get();
  const search = searchContext.get();

  export let data: PageData;

  let initialFilters = searchToFilters(
    data.searchParams,
    data.category,
    data.subCategory,
    data.moods,
    data.genres,
    data.energyLevels,
    data.instruments,
    data.themes,
    data.types,
    data.categories
  );
  let currentType = data.type;
  let currentPage = parseInt(data.searchParams.get('page') ?? '1');
  let searchQuery = data.searchParams.get('search') ?? '';
  let error = false;
  let view: string = 'list';
  let loading = false;
  let shimmerItems = new Array(24).fill(null);
  let showFiltersSheet = false;
  let filtersComponent: SearchFilters;
  let filtersCount = 0;

  let observer: IntersectionObserver;
  let loadMore: HTMLDivElement;
  let isFetchingMore = false;

  let showHero =
    currentPage === 1 &&
    !searchQuery &&
    (!isAnyRemovableFilterSelected(initialFilters) || isSinglePrettyURLCategorySelected(initialFilters));

  onMount(async () => {
    if (browser) {
      // Crear el observer que observa el div loadMore
      observer = new IntersectionObserver(
        async (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && !isFetchingMore && currentPage < results.totalPages) {
              await fetchMoreItems(currentPage);
            }
          }
        },
        {
          root: null, // null = viewport (scroll de ventana)
          rootMargin: '0px',
          threshold: 1.0, // se dispara cuando el div está completamente visible
        }
      );

      if (loadMore) {
        observer.observe(loadMore);
      }

      if (data.user) {
        wishlisted.fetch();
      }
    }
  });
  onDestroy(() => {
    if (observer && loadMore) {
      observer.unobserve(loadMore);
      observer.disconnect();
    }
  });

  // También es recomendable reaccionar si el elemento loadMore cambia (por ejemplo, al re-renderizar)
  // para re-observarlo:
  $: if (observer && loadMore) {
    observer.observe(loadMore);
  }

  /**
   * Updates the URL and calls the server for new results when changing filters or paging.
   *
   * @param event The event that triggered the update.
   *              Can be a Filters object (if triggered by the user changing a filter) or a URLSearchParams object (if triggered by a navigation).
   * @param page The page number to load.
   * @param pushState Whether to push the new URL to the browser history.
   */
  function update(filters: Filters, page: number = 1) {
    error = false;

    let searchParams = filtersToSearch(filters, page, searchQuery);
    let noScroll = true;
    if (currentPage !== page) {
      noScroll = false;
    }
    currentPage = page;
    if (page > 1) {
      searchParams.set('page', page.toString());
    }

    // Update the URL to reflect the new filters.
    let suffix = '';
    const isFirstPageAndNoSearchQuery = page === 1 && !searchQuery;
    const singleChildCategory = maybeGetSingleSelectedChildCategory(filters);
    const isSingleCategoryPrettyURL = (isSinglePrettyURLCategorySelected(filters) || !!singleChildCategory) && isFirstPageAndNoSearchQuery;

    showHero = false;

    if (isSingleCategoryPrettyURL) {
      let category: Tag | null = null;
      let categorization: string | null = null;
      if (filters.moods.length) {
        category = filters.moods[0];
        categorization = 'mood';
      } else if (filters.genres.length) {
        category = filters.genres[0];
        categorization = 'genre';
      } else if (filters.instruments.length) {
        category = filters.instruments[0];
        categorization = 'instrument';
      } else if (filters.themes.length) {
        category = filters.themes[0];
        categorization = 'theme';
      } else {
        category = singleChildCategory ?? filters.categories[0];
        categorization = 'category';
      }
      if (category.parentSlug) {
        suffix = categorization + '/' + category.parentSlug + '/' + category.slug + '/';
      } else {
        suffix = categorization + '/' + category.slug + '/';
      }
    } else {
      suffix = queryToSearch(searchParams);
      // Don't add a ? if there are no search params.
      if (suffix.length > 0) {
        suffix = '?' + suffix;
      }
    }

    loading = true;
    goto(langHref(data.asset) + suffix, { noScroll: noScroll, keepFocus: true, state: { filterChange: true } });
  }

  async function fetchMoreItems(page: number = 1) {
    // if we are on the last page, do nothing
    if (page >= data.results.totalPages) {
      return;
    }

    observer.unobserve(loadMore);

    page = page + 1;

    const urlSearchParams = new URLSearchParams(location.search);
    const sections = getPathParts(removeLocaleSlug(location.pathname));

    const categoryType = sections[1] ?? null;
    const categorySlug = sections[2] ?? null;
    const subCategorySlug = sections[3] ?? null;

    const filters = searchToFilters(
      urlSearchParams,
      categorySlug,
      subCategorySlug,
      data.moods,
      data.genres,
      data.energyLevels,
      data.instruments,
      data.themes,
      data.types,
      data.categories
    );

    const filtersParams = filtersToSearch(filters, page, searchQuery, false);

    const locale = getLocaleFromPath(location.pathname);
    filtersParams.set('lang', locale);
    filtersParams.set('asset', data.type);
    filtersParams.set('pageSize', '50');

    if (categoryType && categorySlug) {
      filtersParams.set('categoryType', categoryType);
      filtersParams.set('categorySlug', categorySlug);
    }
    if (subCategorySlug) {
      filtersParams.set('subCategorySlug', subCategorySlug);
    }

    const stringParams = queryToSearch(filtersParams);

    if (!isFetchingMore) {
      isFetchingMore = true;

      const response = await api<SearchResponse>(fetch, '/v1/search/?' + stringParams);
      if (response) {
        results = {
          ...results,
          items: [...results.items, ...response.items.filter((newItem) => !results.items.some((oldItem) => oldItem.id === newItem.id))],
          totalPages: results.totalPages,
          totalItems: results.totalItems + response.items.length,
        };
        currentPage = page;
      }

      isFetchingMore = false;
      setTimeout(() => {
        if (loadMore) {
          observer.observe(loadMore);
        }
      }, 1000);
    }
  }

  /**
   * This function is called when the user changes the search query in the header,
   * or when switching pages using the pagination component.
   * It updates the URL and calls the server for new results.
   *
   * @param resetScroll Whether to scroll to the top of the page after updating.
   * @param page The page number to load.
   */
  function onChange(resetScroll: boolean = false, page: number = 1, similarTo: number = 0) {
    const searchParams = new URLSearchParams(location.search);
    if (similarTo) {
      searchParams.set('similarTo', similarTo.toString());
    }
    const sections = getPathParts(removeLocaleSlug(location.pathname));
    const filters = searchToFilters(
      searchParams,
      sections[2] ?? null,
      sections[3] ?? null,
      data.moods,
      data.genres,
      data.energyLevels,
      data.instruments,
      data.themes,
      data.types,
      data.categories
    );
    update(filters, page);
    if (resetScroll) {
      scrollToTop();
    }
  }

  /**
   * Updates the initial variables when navigating without manually selecting/removing filters.
   * This is used when clicking on links to specific filter pages, e.g. /rfm/genre/rock/,
   * when switching between asset types or when navigating back/forward in the browser.
   */
  function updateInitialVars() {
    error = false;
    initialFilters = searchToFilters(
      data.searchParams,
      data.category,
      data.subCategory,
      data.moods,
      data.genres,
      data.energyLevels,
      data.instruments,
      data.themes,
      data.types,
      data.categories
    );
    currentPage = parseInt(data.searchParams.get('page') ?? '1');
    searchQuery = data.searchParams.get('search') ?? '';
  }

  function clearSearchQuery() {
    search.set({
      type: data.type,
      query: '',
      placeholder: get(search).placeholder,
      animatedPlaceholder: get(search).animatedPlaceholder,
    });
    searchQuery = '';
    onChange();
  }

  // Store the initial search results and filters in variables we can modify later.
  let results = data.results;
  $: if (data.results) {
    results = data.results;
    loading = false;
  }
  $: if (currentType !== data.type) {
    currentType = data.type;
    updateInitialVars();
    showHero = true;
  }

  // Trigger a search query update when the search query in the header changes.
  $: if ($search.query !== searchQuery) {
    $search.query = searchQuery;
    showHero = false;
  }

  $: if ($search.type !== data.type) {
    $search.type = data.type;
  }

  afterNavigate((navigation) => {
    if (!browser) {
      return;
    }
    if (navigation.type === 'popstate' || (navigation.from && !history.state?.filterChange)) {
      updateInitialVars();
    }
    trackSearch(queryToSearch(data.searchParams));
  });
</script>

<!-- {#if data.init.flags.seasonal && !$user?.subscription.hasTracksAccess}
  <SpringBanner />
{/if} -->
<div class="layout-container">
  <!-- Filters Sidebar -->
  {#if data.type == AssetType.Track || data.type == AssetType.SFX}
    <div class="filters-container">
      <SearchFilters
        bind:this={filtersComponent}
        type={data.type}
        moods={data.moods}
        genres={data.genres}
        energyLevels={data.energyLevels}
        instruments={data.instruments}
        themes={data.themes}
        types={data.types}
        categories={data.categories}
        showBpm={data.type == AssetType.Track}
        showLength={data.type == AssetType.Track}
        initialExpanded={data.type == AssetType.SFX ? 'categories' : ''}
        bind:showFiltersSheet
        bind:filtersCount
        query={searchQuery}
        {initialFilters}
        on:change={(event: any) => update(event.detail)}
        on:removeQuery={clearSearchQuery}
      />
    </div>
  {/if}
  <!-- Main Content Area -->
  <div class="main-content">
    {#if showHero}
      <SearchHero category={results.category} type={data.type} user={$user} />
    {/if}

    <div class:container-ld={data.type == AssetType.Intro} class:content-container={data.type != AssetType.Intro}>
      <TopTabs type={data.type} bind:filtersCount bind:showFiltersSheet onOrderBy={(orderBy) => filtersComponent.setOrderBy(orderBy)} />

      <main>
        {#if error}
          <!-- RIP -->
          Error
        {:else if loading}
          <div class="d-flex flex-column">
            {#each shimmerItems as item}
              <Shimmer height="80px" marginBottom="9px" />
            {/each}
          </div>
        {/if}
        {#if results.totalItems > 0}
          {#if view == 'grid' && data.type == AssetType.Track}
            <TrackGrid type={data.type} tracks={results.items} user={$user} />
          {:else}
            <AssetList type={data.type} tracks={results.items} user={$user} showSimilarIcon={true} />
          {/if}

          {#if isFetchingMore}
            <div class="loading-more">
              <Spinner size={'24px'} micro={true}></Spinner>
              {m['common.loading-more']()}
            </div>
          {/if}
          <div bind:this={loadMore}></div>

          {#if showHero}
            <SeoBottomBanner category={results.category} type={data.type} />
          {/if}
        {:else}
          <!-- Lazy load ZRP -->
          {#await import('$lib/ui/search/search-zrp.svelte') then LazySearchZrp}
            <LazySearchZrp.default
              categoryType={data.type === AssetType.SFX ? CategoryType.SfxCategory : CategoryType.Genre}
              categories={data.categories ?? data.genres}
            />
          {/await}
        {/if}
      </main>
    </div>
  </div>
</div>

<style>
  .layout-container {
    display: flex;
    min-height: calc(100vh - 72px);
  }

  .content-container {
    margin-left: 24px;
    margin-right: 24px;
  }

  @media (max-width: 1050px) {
    .content-container {
      margin-left: 16px;
      margin-right: 16px;
    }
  }

  @media (min-width: 2070px) {
    .content-container {
      max-width: 1520px;
      margin-left: auto;
      margin-right: auto;
      transform: translateX(-126px);
    }
  }

  @media (max-width: 900px) {
    .content-container,
    .container-ld {
      display: block;
      padding: 0;
      margin-left: 14px;
      margin-right: 14px;
      overflow-x: hidden;
      width: auto;
    }
  }

  .filters-container {
    flex-shrink: 0;
    width: 252px;
  }

  .main-content {
    flex: 1;
  }

  .loading-more {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 40px;
    font-size: 14px;
    gap: 8px;
  }
  @media (max-width: 1050px) {
    .layout-container {
      display: block;
    }
  }
</style>
